import { useEffect, useState } from "react";
import {
    ResponsiveContainer,
    BarChart,
    Bar,
    XAxis,
    YAxis,
    CartesianGrid,
    Tooltip,
    Cell,
    PieChart,
    Pie,
} from "recharts";
import { api, formatApiErrorDetail } from "@/lib/api";
import MetricCard from "@/components/dashboard/MetricCard";
import Sparkline from "@/components/dashboard/Sparkline";
import SourceBadge from "@/components/dashboard/SourceBadge";

const nz = new Intl.NumberFormat("en-NZ");
const money = (n) =>
    new Intl.NumberFormat("en-NZ", {
        style: "currency",
        currency: "NZD",
        maximumFractionDigits: 0,
    }).format(n);

export default function FoundersDashboard() {
    const [data, setData] = useState(null);
    const [err, setErr] = useState("");

    useEffect(() => {
        let cancel = false;
        (async () => {
            try {
                const { data } = await api.get("/metrics/founders");
                if (!cancel) setData(data);
            } catch (e) {
                if (!cancel)
                    setErr(formatApiErrorDetail(e?.response?.data?.detail) || e.message);
            }
        })();
        return () => {
            cancel = true;
        };
    }, []);

    if (err) {
        return (
            <div className="p-8">
                <div className="hg-card p-5 border-[#ef4444]/40">
                    <div className="hg-eyebrow text-[#fecaca]">// Error</div>
                    <div className="font-mono-data mt-1">{err}</div>
                </div>
            </div>
        );
    }

    if (!data) return <SkeletonDashboard />;

    const subTotal = data.subscribers.total_active;
    const pieData = data.subscribers.by_tier.map((t) => ({
        name: t.tier,
        value: t.count,
        color: t.color,
    }));

    const regionBars = data.revenue_by_region.map((r) => ({
        region: r.code,
        regionLong: r.region,
        mrr: r.mrr,
    }));

    return (
        <div className="min-h-full px-4 md:px-8 py-6 md:py-8" data-testid="founders-dashboard">
            {/* Topbar */}
            <header className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-3 pb-6 border-b border-white/5">
                <div>
                    <div className="hg-eyebrow mb-2">// Section 01 · Founders</div>
                    <h1 className="font-heading text-3xl md:text-4xl font-bold text-[#FAFAFA] leading-none">
                        Founders Dashboard
                    </h1>
                    <p className="mt-2 text-sm text-[#A1A1AA]">
                        Live revenue, subscribers, churn and valuation across NZ & AU.
                    </p>
                </div>
                <div className="flex items-center gap-4">
                    <SourceBadge source={data.source} note={data.source_note} liveLabel="Live · Stripe" />
                    <StatusChip label="Stripe" ok={data.source === "stripe"} />
                    <div className="hidden md:block hg-eyebrow">
                        <div>// Updated</div>
                        <div className="font-mono-data text-[#FAFAFA] text-xs mt-0.5">
                            {new Date(data.updated_at).toLocaleTimeString()}
                        </div>
                    </div>
                </div>
            </header>

            {/* Row 1: hero metrics */}
            <section className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mt-6">
                <MetricCard
                    label="// MRR · 90d"
                    value={money(data.mrr.value)}
                    sublabel="NZD"
                    deltaPct={data.mrr.delta_pct}
                    accent
                    tag="LIVE"
                    testid="metric-mrr"
                >
                    <Sparkline data={data.mrr.sparkline} />
                </MetricCard>

                <MetricCard
                    label="// ARR"
                    value={money(data.arr.value)}
                    sublabel="NZD / yr"
                    testid="metric-arr"
                />

                <MetricCard
                    label="// Valuation"
                    value={money(data.valuation.value)}
                    sublabel={`ARR × ${data.valuation.multiplier}`}
                    testid="metric-valuation"
                />

                <MetricCard
                    label="// Churn · 90d"
                    value={`${data.churn.value.toFixed(2)}%`}
                    sublabel="monthly"
                    deltaPct={data.churn.delta_pct}
                    deltaLabel="30d"
                    invertDeltaColor
                    testid="metric-churn"
                >
                    <Sparkline
                        data={data.churn.sparkline}
                        color={data.churn.delta_pct > 0 ? "#ef4444" : "#22C55E"}
                    />
                </MetricCard>
            </section>

            {/* Row 2: subscribers breakdown + new subs */}
            <section className="grid grid-cols-1 lg:grid-cols-6 gap-4 mt-4">
                {/* Donut */}
                <div
                    className="hg-card p-5 lg:col-span-3 flex flex-col"
                    data-testid="subscribers-breakdown"
                >
                    <div className="flex items-center justify-between">
                        <div className="hg-eyebrow">// Subscribers · by tier</div>
                        <div className="hg-eyebrow text-[10px] text-[#A1A1AA]">
                            Total {nz.format(subTotal)}
                        </div>
                    </div>

                    <div className="mt-4 grid grid-cols-5 gap-4 items-center">
                        <div className="col-span-2 h-[180px]">
                            <ResponsiveContainer>
                                <PieChart>
                                    <Pie
                                        data={pieData}
                                        dataKey="value"
                                        innerRadius={55}
                                        outerRadius={80}
                                        paddingAngle={2}
                                        stroke="#050505"
                                        strokeWidth={2}
                                        isAnimationActive={false}
                                    >
                                        {pieData.map((entry, i) => (
                                            <Cell key={i} fill={entry.color} />
                                        ))}
                                    </Pie>
                                    <Tooltip
                                        contentStyle={tooltipStyle}
                                        formatter={(v, n) => [nz.format(v), n]}
                                    />
                                </PieChart>
                            </ResponsiveContainer>
                        </div>

                        <div className="col-span-3 space-y-3">
                            {data.subscribers.by_tier.map((tier) => {
                                const share = (tier.count / subTotal) * 100;
                                return (
                                    <div
                                        key={tier.tier}
                                        className="flex items-center gap-3"
                                        data-testid={`tier-row-${tier.tier.toLowerCase()}`}
                                    >
                                        <div
                                            className="w-2 h-6 rounded-sm"
                                            style={{
                                                background: tier.color,
                                                boxShadow:
                                                    tier.tier !== "Free"
                                                        ? `0 0 10px ${tier.color}66`
                                                        : "none",
                                            }}
                                        />
                                        <div className="flex-1">
                                            <div className="flex items-baseline justify-between">
                                                <span className="font-mono-data text-xs uppercase tracking-widest text-[#A1A1AA]">
                                                    {tier.tier}
                                                </span>
                                                <span className="font-mono-data text-sm text-[#FAFAFA]">
                                                    {nz.format(tier.count)}
                                                </span>
                                            </div>
                                            <div className="mt-1 h-[2px] bg-white/5 overflow-hidden rounded-full">
                                                <div
                                                    className="h-full"
                                                    style={{
                                                        width: `${share}%`,
                                                        background: tier.color,
                                                    }}
                                                />
                                            </div>
                                            <div className="hg-eyebrow mt-0.5">
                                                {share.toFixed(1)}%
                                            </div>
                                        </div>
                                    </div>
                                );
                            })}
                        </div>
                    </div>

                    <div className="mt-6 pt-4 border-t border-white/5 flex items-center justify-between">
                        <div>
                            <div className="hg-eyebrow">// Paying</div>
                            <div className="font-mono-data text-xl text-[#22C55E] hg-glow-sm">
                                {nz.format(data.subscribers.paying)}
                            </div>
                        </div>
                        <div>
                            <div className="hg-eyebrow">// Free → Paid conv.</div>
                            <div className="font-mono-data text-xl text-[#FAFAFA]">
                                {(
                                    (data.subscribers.paying / subTotal) *
                                    100
                                ).toFixed(1)}
                                %
                            </div>
                        </div>
                    </div>
                </div>

                {/* New subs */}
                <div className="hg-card p-5 lg:col-span-2 flex flex-col justify-between">
                    <div className="hg-eyebrow">// New subscribers</div>
                    <div className="mt-4 space-y-5">
                        <div data-testid="new-subs-week">
                            <div className="hg-eyebrow text-[10px]">This week</div>
                            <div className="flex items-baseline gap-2">
                                <div className="font-mono-data text-4xl text-[#FAFAFA]">
                                    +{nz.format(data.new_subscribers.this_week)}
                                </div>
                            </div>
                        </div>
                        <div data-testid="new-subs-month">
                            <div className="hg-eyebrow text-[10px]">This month</div>
                            <div className="flex items-baseline gap-2">
                                <div className="font-mono-data text-4xl text-[#22C55E] hg-glow-sm">
                                    +{nz.format(data.new_subscribers.this_month)}
                                </div>
                            </div>
                        </div>
                    </div>
                    <div className="hg-eyebrow mt-6">// Across all tiers · last 30d</div>
                </div>

                {/* Revenue by region */}
                <div
                    className="hg-card p-5 lg:col-span-1 flex flex-col"
                    data-testid="revenue-region"
                >
                    <div className="hg-eyebrow">// Revenue · region</div>
                    <div className="flex-1 mt-3">
                        <ResponsiveContainer width="100%" height={140}>
                            <BarChart
                                data={regionBars}
                                layout="vertical"
                                margin={{ top: 0, right: 10, bottom: 0, left: 0 }}
                            >
                                <CartesianGrid
                                    stroke="rgba(255,255,255,0.04)"
                                    horizontal={false}
                                />
                                <XAxis type="number" hide />
                                <YAxis
                                    type="category"
                                    dataKey="region"
                                    axisLine={false}
                                    tickLine={false}
                                    tick={{
                                        fill: "#A1A1AA",
                                        fontFamily: "JetBrains Mono",
                                        fontSize: 11,
                                    }}
                                    width={30}
                                />
                                <Tooltip
                                    contentStyle={tooltipStyle}
                                    cursor={{ fill: "rgba(34,197,94,0.05)" }}
                                    formatter={(v) => [money(v), "MRR"]}
                                />
                                <Bar
                                    dataKey="mrr"
                                    radius={[0, 2, 2, 0]}
                                    isAnimationActive={false}
                                >
                                    {regionBars.map((b, i) => (
                                        <Cell
                                            key={i}
                                            fill={i === 0 ? "#22C55E" : "#166534"}
                                        />
                                    ))}
                                </Bar>
                            </BarChart>
                        </ResponsiveContainer>
                    </div>
                    <div className="space-y-2 pt-3 border-t border-white/5">
                        {data.revenue_by_region.map((r) => (
                            <div
                                key={r.code}
                                className="flex items-center justify-between"
                                data-testid={`region-${r.code.toLowerCase()}`}
                            >
                                <div className="flex items-center gap-2">
                                    <span className="hg-eyebrow text-[10px]">{r.code}</span>
                                    <span className="text-xs text-[#A1A1AA]">
                                        {r.region}
                                    </span>
                                </div>
                                <span className="font-mono-data text-sm text-[#FAFAFA]">
                                    {money(r.mrr)}
                                </span>
                            </div>
                        ))}
                    </div>
                </div>
            </section>

            {/* Row 3: 90d MRR big chart */}
            <section className="hg-card p-5 mt-4" data-testid="mrr-90d-chart">
                <div className="flex items-center justify-between">
                    <div>
                        <div className="hg-eyebrow">// MRR · last 90 days</div>
                        <div className="font-heading text-xl text-[#FAFAFA] mt-1">
                            Growth trajectory
                        </div>
                    </div>
                    <div className="flex items-center gap-4">
                        <LegendDot color="#22C55E" label="MRR (NZD)" />
                    </div>
                </div>
                <div className="mt-4 h-[220px]">
                    <ResponsiveContainer>
                        <BarChart data={data.mrr.sparkline} margin={{ top: 4, right: 0, left: 0, bottom: 0 }}>
                            <CartesianGrid stroke="rgba(255,255,255,0.04)" vertical={false} />
                            <XAxis
                                dataKey="date"
                                axisLine={false}
                                tickLine={false}
                                tick={{
                                    fill: "#52525b",
                                    fontFamily: "JetBrains Mono",
                                    fontSize: 10,
                                }}
                                tickFormatter={(v) =>
                                    new Date(v).toLocaleDateString("en-NZ", {
                                        month: "short",
                                        day: "numeric",
                                    })
                                }
                                minTickGap={60}
                            />
                            <YAxis
                                axisLine={false}
                                tickLine={false}
                                tick={{
                                    fill: "#52525b",
                                    fontFamily: "JetBrains Mono",
                                    fontSize: 10,
                                }}
                                tickFormatter={(v) => `$${(v / 1000).toFixed(1)}k`}
                                width={48}
                            />
                            <Tooltip
                                contentStyle={tooltipStyle}
                                cursor={{ fill: "rgba(34,197,94,0.06)" }}
                                formatter={(v) => [money(v), "MRR"]}
                                labelFormatter={(l) =>
                                    new Date(l).toLocaleDateString("en-NZ")
                                }
                            />
                            <Bar
                                dataKey="value"
                                fill="#22C55E"
                                radius={[1, 1, 0, 0]}
                                isAnimationActive={false}
                            />
                        </BarChart>
                    </ResponsiveContainer>
                </div>
            </section>
        </div>
    );
}

const tooltipStyle = {
    backgroundColor: "#0A0A0A",
    border: "1px solid rgba(255,255,255,0.1)",
    borderRadius: 2,
    fontFamily: "JetBrains Mono",
    fontSize: 11,
    color: "#FAFAFA",
    padding: "8px 10px",
};

const LegendDot = ({ color, label }) => (
    <div className="flex items-center gap-2">
        <span
            className="w-2 h-2 rounded-full"
            style={{ background: color, boxShadow: `0 0 8px ${color}` }}
        />
        <span className="hg-eyebrow">{label}</span>
    </div>
);

const StatusChip = ({ label, ok }) => (
    <div className="flex items-center gap-1.5" data-testid={`status-${label.toLowerCase().replace(/\s/g, "-")}`}>
        <span
            className={`w-1.5 h-1.5 rounded-full ${ok ? "bg-[#22C55E] hg-pulse-dot" : "bg-[#ef4444]"}`}
        />
        <span className="hg-eyebrow">{label}</span>
    </div>
);

const SkeletonDashboard = () => (
    <div className="p-8 space-y-4">
        <div className="h-12 w-72 bg-white/5 rounded-sm animate-pulse" />
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            {Array.from({ length: 4 }).map((_, i) => (
                <div
                    key={i}
                    className="hg-card h-[160px] p-5"
                >
                    <div className="h-3 w-20 bg-white/5 rounded-sm animate-pulse" />
                    <div className="mt-4 h-8 w-32 bg-white/5 rounded-sm animate-pulse" />
                    <div className="mt-6 h-10 w-full bg-white/5 rounded-sm animate-pulse" />
                </div>
            ))}
        </div>
    </div>
);
